import type { ApiEffect, Page, PageableInfo, Slice } from '../index.h'
import type { TimeRange } from '../interfaces/epg'
import type { MediaUrl } from '../interfaces/generic'
import type {
  CategoryWithChannels,
  CategoryWithChannelsApi,
  EpgInfo,
  GenerateTvPlaylistParams,
  GetAllTvChannelsByPageParams,
  GetPurchasedTvChannelsParams,
  GetRecentlyWatchedChannelsParams,
  TvCategory,
  TvChannel,
  TvChannelUpdated,
  TvChannelUpdateObj,
  TvChannelWithEpg,
  TvEpg,
  TvEpgApiDto,
  TvPlaylist,
} from '../interfaces/tv'

import { attach, createEffect, createStore } from 'effector'
import { produce } from 'immer'
import { abortFeature } from '../lib/abort'

const abortModel = abortFeature()
const { createAbortableEffect } = abortModel

//*
//* EFFECTS
//*

export const { abort } = abortModel

// GET /v3/channels
export const [getAllTvChannelsByPageFx, getAllTvChannelsByPageAbortableFx] =
  createAbortableEffect<GetAllTvChannelsByPageParams, Page<TvChannel>>()

// GET /v3/channels?only-favorites=true
export const getOnlyFavoritesTvChannelsRowFx = attach({
  effect: getAllTvChannelsByPageFx,
  mapParams: () => ({ 'page': 0, 'only-favorites': true }),
})

export const getOnlyFavoritesTvChannelsGridFx = attach({
  effect: getAllTvChannelsByPageFx,
  mapParams: (params?: GetAllTvChannelsByPageParams) => ({
    ...params,
    'page': params?.page || 0,
    'only-favorites': true,
  }),
})

// GET /v3/library/channels/tv
export const [getPurchasedTvChannelsFx, getPurchasedTvChannelsAbortableFx] =
  createAbortableEffect<GetPurchasedTvChannelsParams, Page<TvChannel>>()

export const getPurchasedTvChannelsRowFx = attach({
  effect: getPurchasedTvChannelsAbortableFx,
  mapParams: (params?: GetPurchasedTvChannelsParams) => ({
    ...params,
    page: params?.page || 0,
  }),
})

export const getPurchasedTvChannelsGridFx = attach({
  effect: getPurchasedTvChannelsAbortableFx,
  mapParams: (params?: GetPurchasedTvChannelsParams) => ({
    ...params,
    page: params?.page || 0,
  }),
})

// GET /v3/channels/tv
export const [getTvChannelsByIdBaseFx, getTvChannelsByIdBaseAbortableFx] =
  createAbortableEffect<{ ids: number[] }, TvChannel[]>()

export const getTvChannelsByIdFx = attach({
  effect: getTvChannelsByIdBaseAbortableFx,
})

// GET /v3/channels/tv?categoryId=categoryId
export const [getChannelsByCategoryIdFx, getChannelsByCategoryIdAbortableFx] =
  createAbortableEffect<
    {
      page: number
      count?: number
      categoryId: number
    },
    Page<TvChannel>
  >()

// GET /v3/channels/tv?updatedTimeOnly=true
export const [
  getTvChannelsByIdUpdatedOnlyFx,
  getTvChannelsByIdUpdatedOnlyAbortableFx,
] = createAbortableEffect<{ categoryId: number }, TvChannelUpdated[]>()

// GET /v3/channels/tv/{channelId}/url
export const [getTvChannelUrlFx, getTvChannelUrlAbortableFx] =
  createAbortableEffect<
    {
      channelId: number
      startTime?: number
      endTime?: number
      rewindType?: string
    },
    MediaUrl
  >({ autoAbort: true })

// GET /v3/channels/tv/categories
export const [getTvCategoriesByPageFx, getTvCategoriesByPageAbortableFx] =
  createAbortableEffect<{ page: number; count?: number }, Page<TvCategory>>()

// PATCH /v3/channels/tv/{channelId}
export const updateTvChannelFx: ApiEffect<TvChannelUpdateObj, void> =
  createEffect()

// imitate GET /categories/{categoryId}
export const [getTvCategoryFx, getTvCategoryAbortableFx] =
  createAbortableEffect<{ categoryId: number }, TvCategory | null>()

// GET /v3/channels/tv?only-recommended=true
export const [
  getRealRecommendedTvChannelsFx,
  getRealRecommendedTvChannelsAbortableFx,
] = createAbortableEffect<
  {
    count?: number
  },
  TvChannel[]
>()

export const [
  getCategoriesWithChannelsFx,
  getCategoriesWithChannelsAbortableFx,
] = createAbortableEffect<
  {
    page: number
    count?: number
  },
  Page<CategoryWithChannelsApi>
>()

// POST /media/tv/epg
export const [getEpgInformationFx, getEpgInformationAbortableFx] =
  createAbortableEffect<
    {
      epgIds: string[]
      period: TimeRange
    },
    { epg: TvEpgApiDto; period: TimeRange }
  >()

// POST /media/tv/epg
export const [
  getEpgInformationFormattedFx,
  getEpgInformationFormattedAbortableFx,
] = createAbortableEffect<
  {
    epgIds: string[]
    channels: TvChannel[] | TvChannelWithEpg[]
    period: TimeRange
  },
  { epg: TvEpg; period: TimeRange }
>()

//GET /media/tv/epg

export const [getProgramEPGInfoFx, getProgramEPGInfoAbortableFx] =
  createAbortableEffect<
    {
      epgId: string
      programTitle: string
      start: number
    },
    EpgInfo[]
  >()

export const [getFeaturedChannelsFx, getFeaturedChannelsAbortableFx] =
  createAbortableEffect<void, TvChannel[]>()

// GET /v3/channels/recently-watched
export const [
  getRecentlyWatchedChannelsFx,
  getRecentlyWatchedChannelsAbortableFx,
] = createAbortableEffect<
  GetRecentlyWatchedChannelsParams | undefined | void,
  Slice<TvChannel>
>()

// PUT /v3/channels/recently-watched/{channelId}
export const addRecentlyWatchedChannelFx: ApiEffect<{ id: number }, void> =
  createEffect()

// *
// * STORES
// *

/***************** $tvCategoriesWithChannels *******************/

export const initCategoriesWithChannelsFx = attach({
  effect: getCategoriesWithChannelsAbortableFx,
  mapParams: (params) => ({ ...params, page: 0 }),
})

export const loadCategoriesWithChannelsFx = attach({
  effect: getCategoriesWithChannelsAbortableFx, // use to paginate over subCategories
})

// to paginate over categories use - loadCategoriesWithChannelsFx
export const $tvCategoriesWithChannels = createStore<CategoryWithChannels[]>([])
  .on(initCategoriesWithChannelsFx.doneData, (_, payload) =>
    payload.content.map((page) => ({
      ...page,
      content: [...page.content.content],
    }))
  )
  .on(loadCategoriesWithChannelsFx.doneData, (state, payload) => {
    const newContent = payload.content.map((page) => ({
      ...page,
      content: [...page.content.content],
    }))
    return [...state, ...newContent]
  })

// pagination of main array of categories $tvCategoriesWithChannels
export const $pageInfoCategoriesWithChannels = createStore<PageableInfo | null>(
  null
).on(
  [initCategoriesWithChannelsFx.done, loadCategoriesWithChannelsFx.done],
  (_, { result: { content: _ignored, ...pageInfo }, params }) => ({
    ...pageInfo,
    params,
  })
)

/***************** $tvCategoriesWithChannels *******************/

/***************** $tvPlaylist *******************/

const playlistDefault = { categoryId: null, channels: [], pageInfo: null }

export const [generateTvPlaylistFx, generateTvPlaylistAbortableFx] =
  createAbortableEffect<GenerateTvPlaylistParams, TvPlaylist>()

export const $tvPlaylist = createStore<TvPlaylist>(playlistDefault)

$tvPlaylist.on(generateTvPlaylistFx.doneData, (_, content) => content)

/***************** $tvPlaylist *******************/

/**************** $channelsByCategory *******************/

export const loadChannelsFx = attach({
  effect: getChannelsByCategoryIdAbortableFx,
}) // for pagination over channels
export const loadTvCategoryFx = attach({ effect: getTvCategoryAbortableFx })

export const [initChannelsCategoryFx, initChannelsCategoryAbortableFx] =
  createAbortableEffect<{ categoryId: number }, CategoryWithChannels | null>()

export const $channelsByCategory = createStore<CategoryWithChannels | null>(
  null
)
  .on(initChannelsCategoryFx.doneData, (_ignored, payload) => payload)
  .on(loadChannelsFx.doneData, (state, { content }) => {
    return produce(state, (draft) => {
      if (draft) {
        draft.content = [...draft.content, ...content]
      }
    })
  })

export const $channelsByCategoryPageInfo = createStore<PageableInfo | null>(
  null
).on(
  loadChannelsFx.done,
  (
    _,
    {
      result: { content: _ignored, ...pageInfo },
      params: { signal: _ignored1, ...params },
    }
  ) => ({
    ...pageInfo,
    params,
  })
)

/**************** $channelsByCategory *******************/

/**************** $realRecommendedTvChannels *******************/
export const loadRealRecommendedTvChannelsFx = attach({
  effect: getRealRecommendedTvChannelsAbortableFx,
})
export const $realRecommendedTvChannels = createStore<TvChannel[]>([]).on(
  loadRealRecommendedTvChannelsFx.doneData,
  (_, channelsArr) => channelsArr
)
/**************** $realRecommendedTvChannels *******************/

/**************** $infiniteChannelsCategory *******************/

export const initInfiniteChannelsCategoryFx = attach({
  effect: getAllTvChannelsByPageFx,
  mapParams: (params) => ({ ...params, page: 0 }),
})

const fillFakeCategory = (
  channels: readonly TvChannel[],
  isMoreThanOnePage: boolean
): CategoryWithChannels => ({
  id: 0,
  name: 'All',
  sortOrder: 0,
  parentCategoryId: 0,
  channelIds: [],
  isMoreThanOnePage,
  content: [...channels],
})

// to paginate over categories use - loadInfiniteChannelsCategoryFx
export const $infiniteChannelsCategory =
  createStore<CategoryWithChannels | null>(null).on(
    initInfiniteChannelsCategoryFx.doneData,
    (_, payload) => fillFakeCategory(payload.content, !payload.last)
  )

/**************** $infiniteChannelsCategory *******************/
